import { useCallback, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { clearBonusCode, setBonusCode } from '@/api/auth';
import { BonusStage } from '@/api/common';
import { Modals } from '@/helpers/modals';
import { groupMemberActiveBonus } from '@/helpers/user';
import { useGetCommonStore, useModalsStore } from '@/store';
import { REASON_FOR_UPDATING_USER } from '@/store/commonStore/slices/auth/helpers';

export const useHandleBonus = (code: string | null | undefined) => {
  const useCommonStore = useGetCommonStore();

  const { fetchUser, pendingUser, user } = useCommonStore(
    useShallow((state) => ({
      fetchUser: state.fetchUser,
      pendingUser: state.pendingUser,
      user: state.user,
    }))
  );
  const [bonusPending, setBonusPending] = useState(false);

  const open = useModalsStore.use.open();
  const handleBonus = useCallback(async () => {
    if (user?.canIssueBonuses) {
      setBonusPending(true);
      try {
        code
          ? await setBonusCode({ depositBonusCode: code })
          : await clearBonusCode();

        await fetchUser(REASON_FOR_UPDATING_USER.DEPOSIT);
      } catch (error) {
        console.error(error);
        return;
      } finally {
        setBonusPending(false);
      }
    }

    open(Modals.Deposit);
  }, [user?.canIssueBonuses, code, fetchUser, open]);

  return {
    handleBonus,
    pendingBonus: bonusPending || pendingUser,
  };
};

export const useUserWageringBonuses = () => {
  const useCommonStore = useGetCommonStore();
  const bonuses = useCommonStore.use.bonuses();
  const user = useCommonStore.use.user();

  const groupWageringBonus = useMemo(
    () => groupMemberActiveBonus(user),
    [user]
  );

  const activeWageringBonus = useMemo(() => {
    if (!user) return;
    return bonuses.find(({ stage }) => stage === BonusStage.HandleBets);
  }, [bonuses, user]);

  const activeWageringBonusByCurrency = useMemo(() => {
    if (!user) return;
    return bonuses.find(
      (b) => b.stage === BonusStage.HandleBets && b.currency === user?.currency
    );
  }, [bonuses, user]);

  return {
    groupWageringBonus,
    activeWageringBonus,
    activeWageringBonusByCurrency,
    hasWageringBonus: !!(groupWageringBonus || activeWageringBonus),
    hasWageringBonusByCurrency: !!activeWageringBonusByCurrency,
  };
};

export const useUserCanIssueBonuses = () => {
  const useCommonStore = useGetCommonStore();
  const pendingUser = useCommonStore.use.pendingUser();
  const user = useCommonStore.use.user();

  const isUserCanIssueBonuses = useMemo(
    () => !!user?.canIssueBonuses && !pendingUser,
    [user, pendingUser]
  );

  return isUserCanIssueBonuses;
};
